import {useCallback, useEffect, useMemo, useState} from 'react';

import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';

import {media} from '~/shared/theme/media';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {usePersonalAreaContext} from '~/personalArea/context';
import TenbisCardsList from '~/personalArea/components/Sections/MyTenbis/TenbisCardsList';
import {selectUserData} from '~/shared/store/storeModules/user';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {LoaderContainer} from '~/personalArea/components/Sections/Shared';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import {trackEvent} from '~/shared/services/analytics';

import TenbisCardView from './TenbisCardView';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 24px;
  ${media.minLargeTablet`
    padding: 40px;
  `}
`;

const MyTenbis = () => {
  const [currentMoneycard, setCurrentMoneycard] = useState<MoneyCard>();
  const {isMoneyCardsLoading, moneycards: allCards, activateCardById} = usePersonalAreaContext();
  const userData = useSelector(selectUserData);
  const dispatch = useDispatch();

  const onActivateMoneycardClick = (id: string) => {
    trackEvent('mcActivate_click');
    dispatch(setCurrentModal('pieActivateMoneycardModal', {
      onSuccess: () => {
        setCurrentMoneycard(mc => (mc && mc.id === id ? {...mc, isActivated: true} : mc));
        activateCardById(id);
      },
    }));
  };

  const onMoneycardClick = useCallback((moneycard: MoneyCard) => {
    setCurrentMoneycard(moneycard);
  }, []);

  const onBackButtonClick = useCallback(() => setCurrentMoneycard(undefined), []);

  const moneycards = useMemo(() => allCards?.filter(moneycard => !moneycard.isTenbisCredit) || [], [allCards]);

  useEffect(() => {
    if (!currentMoneycard && !isMoneyCardsLoading && moneycards?.length === 1) {
      setCurrentMoneycard(moneycards[0]);
      
    }
  }, [isMoneyCardsLoading, moneycards, currentMoneycard]);

  if (isMoneyCardsLoading) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  return (
    <Root>
      {currentMoneycard && (
        <TenbisCardView
          currentMoneycard={currentMoneycard}
          onBackButtonClick={moneycards.length > 1 ? onBackButtonClick : null}
          userData={userData}
          onActivateMoneycardClick={onActivateMoneycardClick}
        />
      )}
      {!currentMoneycard && !!moneycards && !!moneycards.length && (
        <TenbisCardsList
          moneycards={moneycards}
          onMoneycardClick={onMoneycardClick}
          onActivateMoneycardClick={onActivateMoneycardClick}
        />
      )}
    </Root>
  );
};

export default MyTenbis;
